import { useAtomValue, useSetAtom } from "jotai";
import styles from "./styles.module.scss";
import {
  getUserGrade,
  isUpdateCorporateProfile,
  userData,
  userProfile,
} from "../../../state";
import IconText from "../../../custom/iconText/iconText";
import avatar from "../../../assets/user.png";
import { ReactComponent as PaperPine } from "../../../assets/papperPine.svg";
import { ReactComponent as Search } from "../../../assets/search-alt.svg";
import Button from "../../../custom/button/button";
import { useState } from "react";
import { App, Tabs, TabsProps } from "antd";
import CompanyDetails from "./companyDetails";
import TopManagement from "./topManagement";
import DocumentUpload from "./documentUpload";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getCorporateProfile, updateCorporateProfile } from "../../../requests";
import { errorMessage } from "../../../utils/errorMessage";
import Spinner from "../../../custom/spinner/spinner";
import { AxiosError } from "axios";

const CorporateProfile = () => {
  const { notification } = App.useApp();
  const profile = useAtomValue(userProfile);
  const user = useAtomValue(userData);
  const setIsProfileEditable = useSetAtom(isUpdateCorporateProfile);
  const grade = getUserGrade(user?.MembershipTypeId!);
  const [tabIndex, setTabIndex] = useState("1");

  const updateProfilePhotoMutation = useMutation({
    mutationKey: ["update-profile-photo"],
    mutationFn: (payload: FormData) => updateCorporateProfile(payload),
  });
  const { data, isLoading, isError, error, refetch } = useQuery({
    queryKey: ["company-details"],
    queryFn: () => getCorporateProfile(user?.MemberId!),
  });
  const companyDetails = data?.Data as CorporateProfile;

  const handleTabChange = (key: string) => {
    setTabIndex(key);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files;
    if (file) {
      uploadPhoto(file[0]);
    }
  };

  const uploadPhoto = async (photo: File) => {
    const formData = new FormData();
    formData.append("MembershipNo", user?.MemberId!);
    formData.append("ApplicantId", user?.ApplicantId);
    formData.append(
      "OrganizationName",
      companyDetails?.OrganizationName || "N/A"
    );
    formData.append("ContactAddress", companyDetails?.ContactAddress || "N/A");
    formData.append("CompanyPhone", companyDetails?.CompanyPhone || "N/A");
    formData.append("OfficerName", companyDetails?.OfficerName || "N/A");
    formData.append("OtherPhone", companyDetails?.OtherPhone || "N/A");
    formData.append("CompanyEmail", companyDetails?.CompanyEmail || "N/A");
    formData.append("OtherEmail", companyDetails?.OtherEmail || "N/A");
    formData.append("BusinessNature", companyDetails?.BusinessNature || "N/A");
    formData.append(
      "OrganizationType",
      companyDetails?.OrganizationType || "N/A"
    );
    formData.append("LogoUrl", companyDetails?.LogoUrl!);
    formData.append("Logo", photo);
    try {
      await updateProfilePhotoMutation.mutateAsync(formData, {
        onSuccess: () => {
          notification.success({
            message: "Success",
            description: "Profile photo updated successfully",
          });
          refetch();
        },
      });
    } catch (error: any) {
      notification.error({
        message: "Error",
        description: errorMessage(error),
      });
    }
  };

  const items: TabsProps["items"] = [
    {
      key: "1",
      label: <p className={styles.tabLabel}>Company Details</p>,
      children: <CompanyDetails companyDetails={companyDetails} />,
    },
    {
      key: "2",
      label: <p className={styles.tabLabel}>Top Management</p>,
      children: <TopManagement />,
    },
    {
      key: "3",
      label: <p className={styles.tabLabel}>Document Upload</p>,
      children: <DocumentUpload />,
    },
  ];

  if (isLoading) {
    return <Spinner width={80} height={80} style={{ marginInline: "auto" }} />;
  }

  if (isError) {
    return <p className="error">{(error as AxiosError)?.message}</p>;
  }

  return (
    <section className={styles.section}>
      <section className={styles.profileWrapper}>
        <article className={styles.article}>
          <div className={styles.pictureContainer}>
            <img
              src={companyDetails?.LogoUrl || avatar}
              alt=""
              className={styles.profile}
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = avatar;
              }}
            />
            <label
              style={{
                cursor: updateProfilePhotoMutation.isPending
                  ? "not-allowed"
                  : "pointer",
              }}
              className={styles.changePhotoBtn}
            >
              {updateProfilePhotoMutation.isPending
                ? "Changing Photo..."
                : "Change Photo"}
              <input
                type="file"
                accept="image/*"
                hidden
                disabled={updateProfilePhotoMutation.isPending}
                onChange={handleFileChange}
              />
            </label>
          </div>
          <div>
            <h2 className={styles.heading}>{profile?.Name?.toUpperCase()}</h2>
            <p className={styles.para}>{grade} Grade Member at CIPM Nigeria</p>
            <span className={styles.iconHolder}>
              <IconText
                heading={profile?.MembershipNo!}
                para="Number"
                icon={<Search />}
              />
              <IconText
                heading={`${profile?.UpgradeScore}`}
                para="Score"
                icon={<PaperPine />}
              />
              {/* {profile?.CreatedDate && (
                <IconText
                  heading={`${getDateWithoutTime(profile?.CreatedDate)}`}
                  para="Date Inducted"
                  icon={<Search />}
                />
              )} */}
            </span>
          </div>
        </article>
        <Button
          text="Edit Profile"
          className={styles.editButton}
          onClick={() => setIsProfileEditable((prev) => !prev)}
        />
        <Tabs
          defaultActiveKey={tabIndex}
          items={items}
          onChange={handleTabChange}
          className={styles.tab}
        />
      </section>
    </section>
  );
};

export default CorporateProfile;
